/**
 * The Inbox: what each Add Officers / regenerate operation did, officer by officer.
 *
 * Portalled to document.body: it's opened from a button that itself portals into
 * the tab bar, so it must render even while HomeTab's own subtree is hidden
 * (App wraps it in a display:none div when Settings is the active tab), and
 * position:fixed does not escape a display:none ancestor.
 */

import React from "react";
import { createPortal } from "react-dom";
import type { OperationMessage } from "@/types/api";
import { messageHasCaution } from "@/components/tabs/home/gridData";
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

interface InboxModalProps {
  /** The messages, newest first. */
  currentVersionMessages: OperationMessage[];
  readMsgKeys: Set<string>;
  setReadMsgKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  expandedMsgKeys: Set<string>;
  setExpandedMsgKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  onClose: () => void;
}

export function InboxModal({
  currentVersionMessages, readMsgKeys, setReadMsgKeys,
  expandedMsgKeys, setExpandedMsgKeys, onClose,
}: InboxModalProps) {
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 4600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "10px",
          maxWidth: "860px",
          width: "100%",
          // Without this, the flexbox "automatic minimum size" rule lets this item's
          // intrinsic content width (the wide table inside, further down) push it past
          // the overlay's bounds instead of shrinking — since position:fixed gives the
          // overlay no scroll of its own, that overflow becomes permanently unreachable
          // rather than just requiring the inner overflowX:auto below to engage.
          minWidth: 0,
          zoom: UI_SCALE, maxHeight: popupMaxH(82),
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
        }}
      >
        {/* Modal title bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderBottom: "2px solid #e2e8f0",
            flexShrink: 0,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1e293b" }}>📬 Inbox</h3>
          <button
            onClick={() => onClose()}
            style={{
              fontSize: "18px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#64748b",
              lineHeight: 1,
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Message list */}
        <div style={{ overflowY: "auto", padding: "12px 16px", flex: 1 }}>
          {currentVersionMessages.length === 0 ? (
            <p style={{ color: "#94a3b8", textAlign: "center", marginTop: 32 }}>No messages yet</p>
          ) : (
            // Reversing the already-reversed list restores original (oldest-first)
            // order — so origIdx below is a stable index into operationMessages.
            [...currentVersionMessages].reverse().map((msg, origIdx) => {
              const msgKey = `${msg.type}-${msg.timestamp}-${origIdx}`;
              const isExpanded = expandedMsgKeys.has(msgKey);
              const isSOS = msg.type === "sos_add";
              const isNewSession = msg.type === "new_session";
              const headerBg = isNewSession ? "#dbeafe" : isSOS ? "#ffc6d9" : "#FDFD96";
              const headerTextColor = isNewSession ? "#1e40af" : isSOS ? "#9d174d" : "#78350f";
              const headerLabel = isNewSession
                ? "New Roster Session"
                : isSOS ? "SOS Officers Added" : "OT Officers Added";
              const hasCaution = messageHasCaution(msg);
              // Cautioned messages start unread and stay that way until the user opens
              // and then closes them; messages with nothing to flag are read on arrival —
              // they don't need to be actively worked through.
              const isUnread = hasCaution && !readMsgKeys.has(msgKey);
              const headerWeight = isUnread ? 700 : 400;

              return (
                <div
                  key={msgKey}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderLeft: isUnread ? `4px solid ${headerTextColor}` : "4px solid transparent",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    overflow: "hidden",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                  }}
                >
                  {/* Header band — always visible, click to toggle */}
                  <div
                    onClick={() => {
                      if (!isExpanded) {
                        // Mark read the moment the message is opened, not on
                        // collapse — closing the whole modal (✕ or backdrop)
                        // without re-collapsing an open message first must not
                        // leave it looking unread again next time.
                        setReadMsgKeys(r => new Set(r).add(msgKey));
                      }
                      setExpandedMsgKeys(prev => {
                        const next = new Set(prev);
                        if (next.has(msgKey)) {
                          next.delete(msgKey);
                        } else {
                          next.add(msgKey);
                        }
                        return next;
                      });
                    }}
                    style={{
                      background: headerBg,
                      color: headerTextColor,
                      padding: "10px 14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontWeight: headerWeight, fontSize: 13, letterSpacing: "0.1px" }}>{headerLabel}</span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: headerWeight,
                          background: "rgba(0,0,0,0.08)",
                          borderRadius: 4,
                          padding: "1px 6px",
                          fontFamily: "monospace",
                        }}
                      >
                        {msg.timestamp}
                      </span>
                      {hasCaution && (
                        <span style={{ fontSize: 13 }} title="Needs a look">⚠️</span>
                      )}
                    </div>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>{isExpanded ? "▲" : "▼"}</span>
                  </div>

                  {/* Collapsible content */}
                  {isExpanded && msg.type === "new_session" && (
                    <div style={{ padding: "12px 14px", fontSize: 13, color: "#1e293b" }}>
                      <p style={{ margin: "0 0 8px" }}>
                        {msg.createdBy ? <strong>{msg.createdBy}</strong> : "Someone"} generated a
                        new roster at {msg.timestamp}. It lives in its own session:
                      </p>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span
                          style={{
                            fontFamily: "monospace", fontSize: 18, fontWeight: 700,
                            letterSpacing: "0.08em", color: "#1e40af",
                            background: "#eff6ff", padding: "4px 10px", borderRadius: 5,
                          }}
                        >
                          {msg.newSessionId}
                        </span>
                        <button
                          onClick={() => void navigator.clipboard.writeText(msg.newSessionId)}
                          style={{
                            padding: "4px 12px", fontSize: 12, fontWeight: 600,
                            background: "#667eea", color: "white", border: "none",
                            borderRadius: 4, cursor: "pointer",
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <p style={{ margin: "8px 0 0", color: "#64748b" }}>
                        This roster is untouched and still yours to edit. Join the new session by
                        ID only if you want the new one.
                      </p>
                    </div>
                  )}
                  {isExpanded && msg.type !== "new_session" && (
                    <div style={{ padding: "12px 14px" }}>
                      {/* Horizontal scroll on narrow screens instead of squeezing/wrapping columns
                          unevenly. Header and row cells are all direct children of ONE grid (not
                          separate per-row grids), so the column tracks are guaranteed to line up —
                          a grid's 1fr tracks only stay in sync with siblings that share its own
                          grid box. */}
                      <div
                        style={{
                          overflowX: "auto",
                          // Without this, iOS/Android can route a touch-drag that starts
                          // here to the modal's own vertical scroller instead — the classic
                          // "horizontal scroller nested in a vertical one" gesture conflict —
                          // leaving the rightmost column reachable only by mouse wheel.
                          touchAction: "pan-x",
                          WebkitOverflowScrolling: "touch",
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isSOS ? "repeat(5, 1fr)" : "repeat(4, 1fr)",
                            gap: "4px 10px",
                            minWidth: 384,
                            borderRadius: "4px",
                            overflow: "hidden",
                          }}
                        >
                          {/* Column headers */}
                          {["Name", ...(isSOS ? ["Deployment"] : []), "Timing", "Remarks", "Your Selection"].map(label => (
                            <div
                              key={label}
                              style={{
                                padding: "5px 8px",
                                background: "#f1f5f9",
                                fontWeight: 700,
                                fontSize: 11,
                                color: "#475569",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {label}
                            </div>
                          ))}
                          {msg.rows.map((row, rowIdx) => {
                            const isPartial = !!row.partialTiming;
                            const isFailed = !row.success;
                            const isSkipped = !!row.skipped;
                            const isDuplicateNote = !!row.existingWindows;
                            const rowBg = isFailed
                              ? "#fff1f2"
                              : isSkipped
                              ? "#f8fafc"
                              : isPartial
                              ? "#fffbeb"
                              : rowIdx % 2 === 0
                              ? "white"
                              : "#f8fafc";
                            const cellStyle: React.CSSProperties = {
                              padding: "5px 8px",
                              background: rowBg,
                              borderBottom: "1px solid #f0f4f8",
                            };

                            // Remarks: the "why" — what triggered this row being listed at all.
                            let remarks: React.ReactNode;
                            if (isDuplicateNote) {
                              remarks = (
                                <span style={{ color: "#b45309", fontWeight: 600 }}>
                                  ⚠️ {row.existingWindows}
                                </span>
                              );
                            } else if (isFailed) {
                              remarks = (
                                <span style={{ color: "#dc2626", fontWeight: 600 }}>
                                  ⚠️ {row.reason || "Assignment failed"}
                                </span>
                              );
                            } else if (isPartial) {
                              remarks = (
                                <span style={{ color: "#b45309", fontWeight: 600 }}>
                                  ⚠️ No counter free (or only for 30 min or less) at {row.partialTiming}
                                </span>
                              );
                            } else {
                              remarks = <span style={{ color: "#94a3b8" }}>—</span>;
                            }

                            // Outcome: the "what happened" — a short, consistently-worded terminal state.
                            let outcome: React.ReactNode;
                            if (isSkipped) {
                              outcome = <span style={{ color: "#6b7280", fontWeight: 700 }}>⏭️ Skipped</span>;
                            } else if (isFailed) {
                              outcome = <span style={{ color: "#dc2626", fontWeight: 700 }}>❌ Skipped</span>;
                            } else if (row.partialTiming && row.removed) {
                              outcome = <span style={{ color: "#ea580c", fontWeight: 700 }}>⏭️ Skipped</span>;
                            } else if (row.adjustedTiming) {
                              outcome = <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Added {row.adjustedTiming}</span>;
                            } else {
                              outcome = <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Added</span>;
                            }

                            // Name: officer key first (always present once submitted) with the
                            // optional user-typed name appended — key alone when there's no name.
                            const nameDisplay = row.officerKey
                              ? row.name ? `${row.officerKey} ${row.name}` : row.officerKey
                              : row.name || "—";

                            return (
                              <React.Fragment key={rowIdx}>
                                <div style={{ ...cellStyle, fontSize: 11, color: "#1e293b", overflowWrap: "break-word" }}>{nameDisplay}</div>
                                {isSOS && (
                                  <div style={{ ...cellStyle, fontSize: 11, color: "#475569", overflowWrap: "break-word" }}>{row.deployment || "—"}</div>
                                )}
                                <div style={{ ...cellStyle, fontFamily: "monospace", fontSize: 11, color: "#374151" }}>{row.timing}</div>
                                <div style={{ ...cellStyle, fontSize: 11 }}>{remarks}</div>
                                <div style={{ ...cellStyle, fontSize: 11, whiteSpace: "nowrap" }}>{outcome}</div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
